import { useRouteError, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { FaHome, FaExclamationTriangle } from 'react-icons/fa';

const ErrorBoundary = () => {
  const error = useRouteError();
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Determine error type and message
  const getErrorDetails = () => {
    if (error?.status === 404) {
      return {
        title: t('errors.not_found') || 'Page Not Found',
        message: t('errors.not_found_message') || 'The page you\'re looking for doesn\'t exist or has been moved.',
        icon: '🔍'
      };
    }
    return {
      title: t('errors.unexpected') || 'Oops! Something went wrong',
      message: error?.statusText || error?.message || t('errors.unexpected_message'),
      icon: '⚠️'
    };
  };

  const errorDetails = getErrorDetails();

  return (
    <div className="flex relative flex-col justify-center items-center min-h-screen bg-gray-900">
      {/* Background decoration - Added negative z-index */}
      <div className="absolute inset-0 z-0 opacity-5">
        <div className="absolute top-1/4 left-1/4 text-9xl text-white -rotate-12">{errorDetails.icon}</div>
        <div className="absolute right-1/4 bottom-1/4 text-9xl text-white rotate-12">{errorDetails.icon}</div>
      </div>

      {/* Main content - Added positive z-index */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="flex relative z-10 flex-col items-center p-8 text-center"
      >
        <div className="mb-6 text-6xl">{errorDetails.icon}</div>
        
        <motion.h1 
          className="mb-4 text-4xl font-bold text-white"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          {errorDetails.title}
        </motion.h1>
        
        <motion.p 
          className="mb-8 text-lg text-white"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          {errorDetails.message}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.4 }}
          className="flex gap-4"
        >
          <button
            onClick={() => navigate('/')}
            className="flex items-center px-6 py-3 text-white rounded-lg shadow-lg transition-all duration-300 bg-stone-800 hover:bg-yellow-700 hover:shadow-xl"
          >
            <FaHome className="mr-2" />
            {t('errors.go_home') || 'Go Home'}
          </button>
          
          <button
            onClick={() => window.location.reload()}
            className="flex items-center px-6 py-3 text-white bg-yellow-800 rounded-lg shadow-lg transition-all duration-300 hover:bg-stone-700 hover:shadow-xl"
          >
            <FaExclamationTriangle className="mr-2" />
            {t('errors.try_again') || 'Try Again'}
          </button>
        </motion.div>

        {error?.stack && process.env.NODE_ENV === 'development' && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="p-4 mt-8 text-sm text-left text-gray-500 bg-gray-100 rounded-lg"
          >
            <pre className="overflow-x-auto">{error.stack}</pre>
          </motion.div>
        )}
      </motion.div>
    </div>
  );
};

export default ErrorBoundary;
